/**
 * GlassPanel - Glassmorphism container component
 * The fundamental building block for all HUD panels
 */

import { forwardRef, type ReactNode, type HTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

type GlowColor = 'primary' | 'secondary' | 'warning' | 'danger' | 'success' | 'none';
type BlurIntensity = 'light' | 'medium' | 'heavy';

interface GlassPanelProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  glow?: GlowColor;
  intensity?: BlurIntensity;
  border?: boolean;
  animate?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const blurClasses: Record<BlurIntensity, string> = {
  light: 'backdrop-blur-sm',
  medium: 'backdrop-blur-md',
  heavy: 'backdrop-blur-xl',
};

const glowClasses: Record<GlowColor, string> = {
  primary: 'shadow-hud-glow',
  secondary: 'shadow-secondary-glow',
  warning: 'shadow-warning-glow',
  danger: 'shadow-danger-glow',
  success: 'shadow-success-glow',
  none: '',
};

const paddingClasses = {
  none: 'p-0',
  sm: 'p-2',
  md: 'p-4',
  lg: 'p-6',
};

export const GlassPanel = forwardRef<HTMLDivElement, GlassPanelProps>(
  (
    {
      children,
      className,
      glow = 'primary',
      intensity = 'medium',
      border = true,
      animate = false,
      padding = 'md',
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          // Base glass effect
          'relative',
          'bg-glass-bg',
          blurClasses[intensity],
          'rounded-hud',

          // Border
          border && 'border border-glass-border',

          // Glow effect
          glowClasses[glow],

          // Padding
          paddingClasses[padding],

          // Animation
          animate && 'animate-glow-pulse',

          // Transitions
          'transition-all duration-300',

          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

GlassPanel.displayName = 'GlassPanel';

/**
 * GlassPanelHeader - Header section for glass panels
 */
interface GlassPanelHeaderProps {
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
}

export function GlassPanelHeader({
  children,
  icon,
  className,
}: GlassPanelHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 mb-3',
        'text-hud-primary font-hud text-xs uppercase tracking-wider',
        className
      )}
    >
      {icon && <span className="text-hud-primary">{icon}</span>}
      <span className="neon-pulse">{children}</span>
    </div>
  );
}

/**
 * GlassPanelContent - Main content area
 */
interface GlassPanelContentProps {
  children: ReactNode;
  className?: string;
}

export function GlassPanelContent({ children, className }: GlassPanelContentProps) {
  return <div className={cn('text-text-primary', className)}>{children}</div>;
}

export default GlassPanel;
